/**
 * Public data-fetching endpoints used by SvelteKit `+page`/`+layout`
 * loaders and components.
 *
 * Each function:
 *   1. Resolves the right URL (local fixture or S3) via `./urls`.
 *   2. Fetches + parses JSON safely via `./fetcher`.
 *   3. Schema-validates the payload (returning the raw payload on
 *      validation failure so the app stays functional with partial data).
 */

import {
  CommentarySchema,
  MatchStatsSchema,
  MatchesSchema,
  SquadSchema,
  validate,
} from './schemas';
import { decorateMatchDates, decorateMatchPath } from './decorateMatches';
import { getFallbackUrl, getUrl } from './urls';

import { EMPTY_DATA } from './constants';
import { cachedFetch } from './cachedFetch';
import parseData from './parseData';
import { safeFetchJson } from './fetcher';

/**
 * Per-match feeds (match-stats / match-commentary) are inherently mutable
 * mid-match — the poller refreshes them every 5 s, and the live-match
 * simulator (toolkit) rewrites them tick-by-tick. We deliberately bypass
 * the `cachedFetch` cache for those two endpoints so each poll sees the
 * latest file on disk / latest S3 payload. The cache stays in place for
 * truly-static feeds (squads, trophies).
 *
 * See bin/toolkit/utils/liveMatchSimulator/ and issue #42.
 */

/**
 * Loads, validates and decorates the tournament matches payload.
 *
 * @param {Function} fetch - SvelteKit's scoped fetch.
 * @returns {Promise<object>} The (post-`parseData`) matches payload with
 *   every match decorated with `isoDate`, `localDateStamp`, `path` and
 *   `label`.
 */
async function loadMatches(fetch) {
  // Try the year-scoped (or remote) source first; fall back to the
  // bundled default so the app stays functional with no published data.
  let matches = await safeFetchJson(fetch, getUrl('matches.json'), null);
  if (matches === null) {
    matches = await safeFetchJson(
      fetch,
      getFallbackUrl('matches.json'),
      EMPTY_DATA.matches
    );
  }

  matches = validate(MatchesSchema, matches, 'matches.json');

  // Ensure the nested `matches.match` array always exists.
  const safeMatches =
    matches && matches.matches && Array.isArray(matches.matches.match) ?
      matches
    : EMPTY_DATA.matches;

  // Date fields before parseData (string-replacements don't touch them).
  safeMatches.matches.match = decorateMatchDates(safeMatches.matches.match);

  const parsed = parseData({
    matches: safeMatches,
    teamStats: null,
    topPlayers: null,
    topTeams: null,
    topPlayerStats: null,
  });

  // Path / label after parseData so the slugs reflect renames (#37).
  if (parsed?.matches?.matches?.match) {
    parsed.matches.matches.match = decorateMatchPath(
      parsed.matches.matches.match
    );
  }

  return parsed;
}

/**
 * Retrieves the top-level data object used by every route's `+layout` /
 * `+page` loader. Today this is just the matches payload; the keys for
 * `teamStats`, `topPlayers`, `topTeams`, `topPlayerStats` are kept on
 * the returned object so existing callers (and `parseData` downstream)
 * see a stable shape.
 *
 * @param {Object} options
 * @param {Function} options.fetch - SvelteKit's scoped fetch.
 * @returns {Promise<Object>}
 */
export async function getData({ fetch }) {
  return loadMatches(fetch);
}

/**
 * Retrieves the squad data for a given team.
 *
 * @param {Object} options - The options for fetching the squad data.
 * @param {Function} options.fetch - The fetch function used to make the API request.
 * @param {Object} options.team - The team object containing the team id.
 * @returns {Promise<Object>} - A promise that resolves to the squad data.
 */
export async function getSquad({ fetch, team }) {
  const url = getUrl(`squads/${team.id}.json`);
  // Cached per-URL (issue #27): squad rosters are effectively static for
  // the tournament, so re-visiting a team page within a session reuses
  // the same JSON without a second network round-trip.
  const data = await cachedFetch(fetch, url, null);
  const validated = validate(SquadSchema, data, `squads/${team.id}.json`);
  // Run through `parseData` so string-replacements + `latinize` apply to
  // player / coach / assistant names (e.g. `C. Pot`, `D. Gorré`).
  return validated ? parseData(validated) : validated;
}

/**
 * Retrieves the commentary data for a specific match.
 *
 * @param {Object} options - The options for retrieving the commentary.
 * @param {Function} options.fetch - The fetch function used to make the API request.
 * @param {string} options.matchId - The ID of the match for which to retrieve the commentary.
 * @returns {Promise<Object|null>} - A promise that resolves to the commentary data, or null if an error occurs.
 */
export async function getCommentary({ fetch, matchId }) {
  const url = getUrl(`match-commentary/${matchId}.json`);
  // Bypass cachedFetch — commentary changes mid-match (live OPTA feed, or
  // the live-match simulator in dev). See header note above + issue #42.
  const data = await safeFetchJson(fetch, url, null);
  const validated = validate(
    CommentarySchema,
    data,
    `match-commentary/${matchId}.json`
  );
  // Apply string-replacements + `latinize` to player / team names in
  // commentary text (e.g. `Gorré` → `Gorre`).
  return validated ? parseData(validated) : validated;
}

/**
 * Retrieves the match statistics for a given match ID.
 * @param {Object} options - The options for fetching the match statistics.
 * @param {Function} options.fetch - The fetch function used to make the API request.
 * @param {string} options.matchId - The ID of the match to retrieve the statistics for.
 * @returns {Promise<Object|null>} - A promise that resolves to the match statistics data, or null if an error occurs.
 */
export async function getMatchStats({ fetch, matchId }) {
  const url = getUrl(`match-stats/${matchId}.json`);
  // Bypass cachedFetch — match-stats changes mid-match (live OPTA feed, or
  // the live-match simulator in dev). See header note above + issue #42.
  const data = await safeFetchJson(fetch, url, null);
  const validated = validate(
    MatchStatsSchema,
    data,
    `match-stats/${matchId}.json`
  );
  // Apply string-replacements + `latinize` to player / team names in
  // match stats (lineups, scorers, bookings, etc.).
  return validated ? parseData(validated) : validated;
}

/**
 * Retrieves the historical trophies feed (past tournament winners and
 * runners-up). Used by the team page's "trophy cabinet" component.
 *
 * Hits the same local-vs-S3 plumbing as every other endpoint — never goes
 * directly to S3, so dev / preview / Reuters.com all behave consistently.
 *
 * The trophies payload sits outside the four schemas we strictly validate,
 * so we hand back the raw JSON. Returns `null` on any fetch / parse failure.
 *
 * @param {Object} options
 * @param {Function} [options.fetch] - The fetch function to use. Defaults to the global `fetch` so this endpoint is callable from inside Svelte components (which don't get SvelteKit's scoped `fetch`).
 * @returns {Promise<Object|null>}
 */
export async function getTrophies({ fetch: fetchFn = fetch } = {}) {
  const url = getUrl('trophies.json');
  // Cached per-URL (issue #27): historical trophies feed is fully static.
  const data = await cachedFetch(fetchFn, url, null);
  // Apply string-replacements + `latinize` to historical team / winner
  // names so the trophy cabinet renders plain ASCII alongside the rest
  // of the app.
  return data ? parseData(data) : data;
}
